import { Widget } from '@/components/widget';
import { useTheme } from '@/hooks/use-theme';
import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';

const WIDGET_ID = 'HORIZONTAL_BAR_WIDGET';

type SimplePoint = { name: string; value: number };
type StackedRow = { name: string } & Record<string, number>;

type Props =
  | {
      title?: string;
      max?: number;
      // non-stacked
      data?: SimplePoint[];
      stackKeys?: never;
    }
  | {
      title?: string;
      max?: number;
      // stacked
      data?: StackedRow[];
      stackKeys: string[]; // 👈 keys to stack, e.g. ['running','queued']
    };

export function HorizontalBarChartWidget(props: Props) {
  const { theme } = useTheme('light');
  const axisTextColor = theme === 'light' ? '#000' : '#fff';
  const gridLineColor =
    theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)';

  const options = useMemo(() => {
    const palette = [
      'rgb(199, 210, 254)',
      'rgb(165, 180, 252)',
      'rgb(129, 140, 248)',
      'rgb(99, 102, 241)',
      'rgb(79, 70, 229)',
      'rgb(67, 56, 202)',
      'rgb(55, 48, 163)',
      'rgb(49, 46, 129)',
    ];

    const data = (props.data ?? []) as any[];
    const names = data.map((d) => d.name);

    const stacked = 'stackKeys' in props && Array.isArray(props.stackKeys);

    const series = stacked
      ? props.stackKeys.map((key) => ({
          name: key,
          type: 'bar',
          stack: 'total', // 👈 THIS makes it stacked
          data: data.map((row) => Number(row[key] ?? 0)),
          barMaxWidth: 22,
        }))
      : [
          {
            name: 'Value',
            type: 'bar',
            data: data.map((d) => Number(d.value ?? 0)),
            barMaxWidth: 22,
            showBackground: true,
            backgroundStyle: {
              color:
                theme === 'light'
                  ? 'rgba(0,0,0,0.05)'
                  : 'rgba(255,255,255,0.08)',
            },
            // per-bar colors for non-stacked mode
            itemStyle: {
              borderRadius: [0, 6, 6, 0],
              color: (params: any) => palette[params.dataIndex % palette.length],
            },
          },
        ];

    return {
      color: palette, // in stacked mode, palette applies per-series automatically
      grid: { left: 16, right: 16, top: 16, bottom: 16, containLabel: true },
      legend: stacked
        ? { show: true, textStyle: { color: axisTextColor } }
        : { show: false },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: {
        type: 'value',
        min: 0,
        max:
          typeof props.max === 'number' && props.max > 0
            ? props.max
            : (value: { max: number }) => Math.ceil(value.max * 1.1),
        axisLabel: { color: axisTextColor },
        splitLine: { lineStyle: { color: gridLineColor } },
      },
      yAxis: {
        type: 'category',
        data: names,
        axisLabel: { color: axisTextColor },
        axisTick: { show: false },
      },
      series,
    };
  }, [props, theme, axisTextColor, gridLineColor]);

  return (
    <Widget title={props.title ?? 'Statistics'} id={WIDGET_ID}>
      <ReactECharts option={options} style={{ width: '100%' }} notMerge={false} />
    </Widget>
  );
}